import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { CheckCircleIcon } from '@phosphor-icons/react/CheckCircle';
import { MinusCircleIcon } from '@phosphor-icons/react/MinusCircle';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AudienceListType, FormSegment } from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import { ListType } from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import type { AgeType } from '../../../hooks/useDemographicsOptions.ts';

import { DemographicsAgeSegmentsListItem } from './DemographicsAgeSegmentsListItem.tsx';
import { segmentsToRangeGroups } from './demographicsUtils.ts';

type BaseDemographicsProps = {
  listType: AudienceListType;
  ageSegments: FormSegment[] | undefined;
};

type DemographicsProps =
  | (BaseDemographicsProps & { readOnly: true })
  | (BaseDemographicsProps & {
      readOnly?: false;
      onAgeDelete: (segment: AgeType) => void;
    });

export const DemographicsAgeSegmentsList = ({ listType, ageSegments, ...rest }: DemographicsProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const countAllSegments = useMemo(() => segmentsToRangeGroups(ageSegments).length - 1, [ageSegments]);

  if (!ageSegments?.length) {
    return null;
  }

  const listTypeLabel = listType === 'includes' ? t('included') : t('excluded');

  const readOnly = rest.readOnly === true;
  const onAgeDelete = !readOnly && 'onAgeDelete' in rest ? rest.onAgeDelete : undefined;

  return (
    <>
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
        }}
      >
        {listTypeLabel}
      </Typography>
      <Grid container>
        <Grid sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
          {listType === ListType.INCLUDES ? (
            <CheckCircleIcon data-test="check-icon" color={theme.palette.success.main} size={20} />
          ) : (
            <MinusCircleIcon data-test="block-icon" color={theme.palette.error.main} size={20} />
          )}
        </Grid>

        {segmentsToRangeGroups(ageSegments).map((range, index) => (
          <Grid key={range.min}>
            <DemographicsAgeSegmentsListItem
              range={range}
              onAgeDelete={onAgeDelete}
              isLastItem={index === countAllSegments}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
};
